import type { GameState, GameAction, Player } from '../game/types';
import { getStealTargets } from '../game/rules';
import { FarmHouse } from './FarmHouse';
import { Icons } from './Icons';

type Props = {
  state: GameState;
  dispatch: React.Dispatch<GameAction>;
  onPlaySfx: (type: 'flip' | 'steal') => void;
};

export function ActionModals({ state, dispatch, onPlaySfx }: Props) {
  if (state.modalType === 'action') {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 animate-pop-in">
        <div className="bg-white p-6 rounded-3xl shadow-2xl max-w-sm w-full text-center border-4 border-green-400">
          <h2 className="text-2xl font-black text-green-600 mb-2">✨ 配對成功！</h2>
          <p className="text-gray-600 mb-6 font-bold">請選擇獎勵行動：</p>
          <div className="space-y-3">
            <button
              onClick={() => {
                onPlaySfx('flip');
                dispatch({ type: 'HARVEST' });
              }}
              className="w-full bg-green-500 hover:bg-green-600 border-b-4 border-green-700 text-white py-3 rounded-xl font-bold btn-3d flex items-center justify-center gap-2"
            >
              <Icons.Apple /> 採收 (+2)
            </button>
            <button
              onClick={() => {
                const targets = getStealTargets(state.players, state.turn, state.lastVictimId);
                if (targets.length === 0) {
                  alert('無人可偷！');
                  onPlaySfx('flip');
                  dispatch({ type: 'HARVEST' });
                } else {
                  dispatch({ type: 'OPEN_STEAL_MODAL' });
                }
              }}
              className="w-full bg-orange-500 hover:bg-orange-600 border-b-4 border-orange-700 text-white py-3 rounded-xl font-bold btn-3d"
            >
              😈 偷水果
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (state.modalType === 'steal') {
    const activeCount = state.players.filter((p) => !p.isEliminated).length;
    const ignoreProtection = activeCount <= 2;
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 animate-pop-in">
        <div className="bg-white p-6 rounded-3xl shadow-2xl max-w-md w-full text-center border-4 border-orange-400">
          <h3 className="text-xl font-black text-gray-800 mb-4">目標是誰的農場？</h3>
          <div className="grid grid-cols-2 gap-4">
            {state.players.map((p: Player) => {
              if (p.id === state.turn || p.isEliminated || p.fruits <= 0) return null;
              const isProtected = !ignoreProtection && p.id === state.lastVictimId;
              if (isProtected) {
                return (
                  <button
                    key={p.id}
                    disabled
                    className="bg-gray-100 p-3 rounded-xl border-2 border-gray-200 opacity-60 cursor-not-allowed relative"
                  >
                    <div className="absolute top-2 right-2 text-blue-500">
                      <Icons.Shield />
                    </div>
                    <div className="font-bold text-gray-500">{p.name}</div>
                    <div className="text-xs text-blue-500 font-bold mt-1">保護中</div>
                  </button>
                );
              }
              return (
                <button
                  key={p.id}
                  onClick={() => {
                    onPlaySfx('steal');
                    dispatch({ type: 'STEAL', targetId: p.id });
                  }}
                  className={`p-3 rounded-xl border-2 border-transparent hover:border-red-400 transition flex flex-col items-center bg-white shadow-md hover:shadow-lg hover:-translate-y-1 duration-200 ${p.bgClass}`}
                >
                  <div className="font-bold text-gray-700 mb-1">{p.name}</div>
                  <div className="w-16 h-16">
                    <FarmHouse colorClass={p.farmColor} />
                  </div>
                  <div className="mt-2 bg-white/80 px-2 rounded-full text-sm text-gray-600 font-bold shadow-sm">
                    🍎 {p.fruits}
                  </div>
                </button>
              );
            })}
          </div>
          <button
            onClick={() => dispatch({ type: 'CLOSE_STEAL_MODAL' })}
            className="mt-6 text-gray-400 hover:text-gray-600 font-bold text-sm"
          >
            取消返回
          </button>
        </div>
      </div>
    );
  }

  if (state.modalType === 'decision') {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 animate-pop-in">
        <div className="bg-white p-6 rounded-3xl shadow-2xl max-w-sm w-full text-center border-4 border-blue-400">
          <h3 className="text-xl font-black text-gray-800 mb-2">行動完成</h3>
          <p className="text-gray-500 mb-6 font-bold text-sm">要繼續挑戰嗎？</p>
          <div className="flex gap-4">
            <button
              onClick={() => dispatch({ type: 'CONTINUE_TURN' })}
              className="flex-1 bg-blue-500 hover:bg-blue-600 border-b-4 border-blue-700 text-white py-2 rounded-xl font-bold btn-3d"
            >
              🔄 繼續翻
            </button>
            <button
              onClick={() => dispatch({ type: 'END_TURN' })}
              className="flex-1 bg-gray-400 hover:bg-gray-500 border-b-4 border-gray-600 text-white py-2 rounded-xl font-bold btn-3d"
            >
              🛑 結束回合
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
